import logger from '../utils/logger';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import '../style/OrderWorklist.css';
import { GlobalStateContext } from '../utils/GlobalStateContext';
import { dateOfBirth, getUserId, capitalize } from '../utils/utils';
import GearsSpinner from './customComponents/GearsSpinner';
import { CalendarPlus, CalendarOff, ListFilter, Home, UserCheck, Calendar, Scan, FlaskConical, StickyNote, Search, X, ArrowUp } from 'lucide-react';
import PageTopBar from './customComponents/PageTopBar';
import SchedulingForm from '../utils/useScheduling';

const TABS = [
  { id: 'Radiology', label: 'Radiology', filterLabel: 'Filter Modality',    typeHeader: 'Modality',      Icon: Scan },
  { id: 'Lab',       label: 'Lab',        filterLabel: 'Filter Specimen',    typeHeader: 'Specimen Type', Icon: FlaskConical },
];

const SORT_COLUMNS = [
  { key: 'order_id',            label: 'Order #' },
  { key: 'patient',              label: 'Patient' },
  { key: 'exam_ordered',        label: 'Exam' },
  { key: 'type',                 label: null }, // label supplied dynamically via currentTab.typeHeader
  { key: 'created_at',          label: 'Date Ordered' },
  { key: 'ordering_clinician',  label: 'Clinician' },
  { key: 'priority',             label: 'Priority' },
  { key: 'status',               label: 'Status' },
];

const OrderWorklist = ({ patientId, userRole }) => {
  const { currentPatient, currentUser } = useContext(GlobalStateContext);
  const location = useLocation();
  const autoOpenHandled = useRef(false);
  const [activeTab, setActiveTab]                         = useState('Radiology');
  const [orders, setOrders]                               = useState([]);
  const [selectedPatient, setSelectedPatient]             = useState(null);
  const [showModal, setShowModal]                         = useState(false);
  const [showCancelModal, setShowCancelModal]             = useState(false);
  const [selectedOrderForCancel, setSelectedOrderForCancel] = useState(null);
  const [cancelReason, setCancelReason]                   = useState('');
  const [loading, setLoading]                             = useState(false);
  const [cancelLoading, setCancelLoading]                 = useState(false);
  const [selectedTypes, setSelectedTypes]                 = useState([]);
  const [showFilterDropdown, setShowFilterDropdown]       = useState(false);
  const [searchTerm, setSearchTerm]                       = useState('');
  const [modalTab, setModalTab]                           = useState('schedule');
  const [orderNotes, setOrderNotes]                       = useState([]);
  const [notesLoading, setNotesLoading]                   = useState(false);
  const [newNote, setNewNote]                             = useState('');
  const [addingNote, setAddingNote]                       = useState(false);
  const [sortField, setSortField]                         = useState(null);
  const [sortDirection, setSortDirection]                 = useState('asc');
  const [patientSortMode, setPatientSortMode]             = useState('first');
  const user = getUserId(currentUser);

  const BASE_URL = process.env.REACT_APP_API_URL;

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}/orders/ordersWorklist?user=${user}&type=${currentUser.type}`
      );
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
        return data;
      } else {
        toast.error('Failed to fetch orders');
      }
    } catch (error) {
      logger.error('Error fetching orders:', error);
      toast.error('An error occurred while fetching orders');
    } finally {
      setLoading(false);
    }
    return [];
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.warning('Please provide a reason for canceling this order.');
      return;
    }
    if (!user) {
      toast.error('Session expired. Please log out and log back in.');
      return;
    }
    try {
      setCancelLoading(true);
      const response = await fetch(
        `${BASE_URL}/orders/cancelOrder/${selectedOrderForCancel.order_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status:       'cancelled',
            order_type:   selectedOrderForCancel.order_type,
            cancel_reason: cancelReason,
            cancelled_by: user,
            cancelled_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
          }),
        }
      );
      if (response.ok) {
        toast.success('Order cancelled successfully');
        setShowCancelModal(false);
        setCancelReason('');
        setSelectedOrderForCancel(null);
        fetchOrders();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to cancel order. Please try again.');
      }
    } catch (error) {
      logger.error('Error canceling order:', error);
      toast.error('An error occurred while canceling the order');
    } finally {
      setCancelLoading(false);
    }
  };

  const fetchNotes = async (orderId, orderType) => {
    try {
      setNotesLoading(true);
      const response = await fetch(
        `${BASE_URL}/orders/worklistNotes/${orderId}?order_type=${orderType}&user=${user}&type=${currentUser.type}`
      );
      if (response.ok) {
        setOrderNotes(await response.json());
      } else {
        toast.error('Failed to load notes');
      }
    } catch (error) {
      logger.error('Error fetching notes:', error);
      toast.error('An error occurred while loading notes');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      setAddingNote(true);
      const response = await fetch(`${BASE_URL}/orders/worklistNotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: selectedPatient.order_id,
          order_type: selectedPatient.order_type,
          note: newNote,
          user,
          type: currentUser.type,
        }),
      });
      if (response.ok) {
        setNewNote('');
        await fetchNotes(selectedPatient.order_id, selectedPatient.order_type);
      } else {
        toast.error('Failed to save note');
      }
    } catch (error) {
      logger.error('Error adding note:', error);
      toast.error('An error occurred while saving the note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleCloseModal = async () => {
    if (newNote.trim() && selectedPatient) {
      await handleAddNote();
    }
    setShowModal(false);
    setModalTab('schedule');
    setOrderNotes([]);
    setNewNote('');
  };

  useEffect(() => {
    const init = async () => {
      const data = await fetchOrders();
      const { orderId, orderType } = location.state || {};
      if (orderId && data && !autoOpenHandled.current) {
        autoOpenHandled.current = true;
        const target = data.find(o => o.order_id === Number(orderId));
        if (target) {
          if (orderType) setActiveTab(orderType === 'Lab' ? 'Lab' : 'Radiology');
          setSelectedPatient(target);
          setShowModal(true);
        }
      }
    };
    init();
  }, []);

  // Split orders by worklist type
  const radiologyOrders = orders.filter(o => o.order_type === 'Radiology' || o.order_type === 'Clinic');
  const labOrders       = orders.filter(o => o.order_type === 'Lab');

  const tabOrderMap = { Radiology: radiologyOrders, Lab: labOrders };
  const activeOrders = tabOrderMap[activeTab] || [];

  const currentTab   = TABS.find(t => t.id === activeTab);
  const availableTypes = [...new Set(activeOrders.map(o => o.type).filter(Boolean))].sort();
  const typeCounts     = activeOrders.reduce((acc, o) => {
    if (o.type) acc[o.type] = (acc[o.type] || 0) + 1;
    return acc;
  }, {});

  const toggleType = (t) =>
    setSelectedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  // Search matches the patient's name (in either order, so "smith john" finds
  // the same row as "john smith"), the order id, or the ordering provider —
  // the things a tech has in hand when someone calls about a specific order.
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const matchesSearch = (order) => {
    if (!normalizedSearch) return true;
    const first = (order.first_name || '').toLowerCase();
    const last  = (order.last_name  || '').toLowerCase();
    const haystacks = [
      `${first} ${last}`,
      `${last} ${first}`,
      String(order.order_id),
      (order.ordering_clinician || '').toLowerCase(),
      (order.referring_provider || '').toLowerCase(),
    ];
    return haystacks.some(h => h.includes(normalizedSearch));
  };

  const filteredOrders = activeOrders
    .filter(o => selectedTypes.length === 0 || selectedTypes.includes(o.type))
    .filter(matchesSearch);

  const handleSort = (field) => {
    if (field === 'patient' && sortField === 'patient') {
      // Cycle: first name asc -> first name desc -> last name asc -> last name desc -> first name asc...
      if (patientSortMode === 'first' && sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (patientSortMode === 'first' && sortDirection === 'desc') {
        setPatientSortMode('last');
        setSortDirection('asc');
      } else if (patientSortMode === 'last' && sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setPatientSortMode('first');
        setSortDirection('asc');
      }
      return;
    }
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
      if (field === 'patient') setPatientSortMode('first');
    }
  };

  const getSortValue = (order, field) => {
    if (field === 'patient') {
      return patientSortMode === 'last'
        ? `${order.last_name || ''} ${order.first_name || ''}`.toLowerCase()
        : `${order.first_name || ''} ${order.last_name || ''}`.toLowerCase();
    }
    if (field === 'created_at') return new Date(order.created_at).getTime() || 0;
    const value = order[field];
    return typeof value === 'string' ? value.toLowerCase() : (value ?? '');
  };

  const sortedOrders = sortField
    ? [...filteredOrders].sort((a, b) => {
        const aValue = getSortValue(a, sortField);
        const bValue = getSortValue(b, sortField);
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      })
    : filteredOrders;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedTypes([]);
    setShowFilterDropdown(false);
  };

  // Counts on the tabs stay honest while searching, so a tech who searches on the
  // Radiology tab can see the match is actually sitting on the Lab tab.
  const tabCount = (tabId) => (tabOrderMap[tabId] || []).filter(matchesSearch).length;

  return (
    <div className="order-worklist-page">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <PageTopBar links={[
        { label: 'Home',       to: '/home',       icon: Home },
        { label: 'Check-In',   to: '/checkIn',    icon: UserCheck },
        { label: 'Scheduling', to: '/scheduling', icon: Calendar },
      ]} />

      <div className="order-worklist-content">
        {loading ? (
          <GearsSpinner alt="Loading" style={{ width: '200px', height: '200px' }} />
        ) : (
          <div className="supervisory-review-container">
            <h2>Order Worklist</h2>

            {/* ── Tab Navigation ── */}
            <div className="worklist-tabs">
              {TABS.map(({ id, label, Icon }) => {
                const count = tabCount(id);
                return (
                  <button
                    key={id}
                    className={`worklist-tab${activeTab === id ? ' active' : ''}`}
                    onClick={() => handleTabChange(id)}
                  >
                    <Icon size={15} />
                    {label}
                    <span className="worklist-tab-badge">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* ── Filter Bar ── */}
            <div className="worklist-filter-bar">
              <div className="worklist-search-control">
                <Search size={15} className="worklist-search-icon" />
                <input
                  type="text"
                  className="worklist-search-input"
                  placeholder="Search patient name, order ID, or ordering/referring provider..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="worklist-search-clear"
                    title="Clear search"
                    onClick={() => setSearchTerm('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="worklist-filter-control">
                <button
                  className={`worklist-filter-button${showFilterDropdown ? ' active' : ''}`}
                  title={currentTab?.filterLabel}
                  onClick={() => setShowFilterDropdown(prev => !prev)}
                >
                  <ListFilter size={15} />
                  <span>{currentTab?.filterLabel}</span>
                  {selectedTypes.length > 0 && (
                    <span className="worklist-filter-badge">{selectedTypes.length}</span>
                  )}
                </button>

                {showFilterDropdown && availableTypes.length > 0 && (
                  <div className="worklist-filter-dropdown">
                    {availableTypes.map(t => (
                      <label
                        key={t}
                        className={`worklist-filter-chip${selectedTypes.includes(t) ? ' selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes(t)}
                          onChange={() => toggleType(t)}
                        />
                        {t}
                        <span className="worklist-filter-count">{typeCounts[t]}</span>
                      </label>
                    ))}
                    {selectedTypes.length > 0 && (
                      <button
                        className="worklist-filter-clear"
                        onClick={() => setSelectedTypes([])}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Table ── */}
            {filteredOrders.length === 0 ? (
              <p className="worklist-empty">
                {normalizedSearch
                  ? `No orders match "${searchTerm.trim()}" on this tab.`
                  : 'No orders to display.'}
              </p>
            ) : (
              <table className="supervisory-table">
                <thead>
                  <tr>
                    {SORT_COLUMNS.map(({ key, label }) => (
                      <th
                        key={key}
                        className="worklist-sortable-header"
                        onClick={() => handleSort(key)}
                      >
                        <span className="worklist-sortable-header-inner">
                          {key === 'type' ? currentTab?.typeHeader : label}
                          {sortField === key && (
                            <ArrowUp
                              size={13}
                              className="worklist-sort-arrow"
                              style={{ transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none' }}
                            />
                          )}
                        </span>
                      </th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOrders.map((order, index) => (
                    <tr key={index}>
                      <td className="worklist-order-id">{order.order_id}</td>
                      <td>
                        {sortField === 'patient' && patientSortMode === 'last'
                          ? `${order.last_name}, ${order.first_name}`
                          : `${order.first_name} ${order.last_name}`}
                      </td>
                      <td>{order.exam_ordered}</td>
                      <td>
                        {order.type ? (
                          <span className="worklist-type-pill">{order.type}</span>
                        ) : '—'}
                      </td>
                      <td>{dateOfBirth(order.created_at)}</td>
                      <td>{order.ordering_clinician || 'Referral'}</td>
                      <td>
                        <span className={`worklist-priority-badge priority-${(order.priority || '').toLowerCase()}`}>
                          {order.priority}
                        </span>
                      </td>
                      <td>{capitalize(order.status)}</td>
                      <td>
                        <div className="worklist-button-row">
                          <button
                            className="orderworklist-contact-button"
                            title="Add New Appointment for Order"
                            onClick={() => { setSelectedPatient(order); setShowModal(true); }}
                          >
                            <CalendarPlus size={16} />
                          </button>
                          <button
                            className="orderworklist-cancel-button"
                            title="Cancel Order"
                            onClick={() => { setSelectedOrderForCancel(order); setShowCancelModal(true); }}
                            disabled={order.status === 'cancelled' || order.order_type === 'Clinic'}
                          >
                            <CalendarOff size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ── Schedule Modal ── */}
            {showModal && selectedPatient && (
              <div className="orderworklist-modal-overlay">
                <div className="orderworklist-modal-content">
                  <h3>{selectedPatient.first_name} {selectedPatient.last_name}</h3>

                  {/* Modal Tabs */}
                  <div className="owlist-modal-tabs">
                    <button
                      className={`owlist-modal-tab${modalTab === 'schedule' ? ' active' : ''}`}
                      onClick={() => setModalTab('schedule')}
                    >
                      <Calendar size={14} /> Schedule
                    </button>
                    <button
                      className={`owlist-modal-tab${modalTab === 'notes' ? ' active' : ''}`}
                      onClick={() => {
                        setModalTab('notes');
                        fetchNotes(selectedPatient.order_id, selectedPatient.order_type);
                      }}
                    >
                      <StickyNote size={14} /> Notes
                      {orderNotes.length > 0 && (
                        <span className="owlist-modal-tab-badge">{orderNotes.length}</span>
                      )}
                    </button>
                  </div>

                  {/* Schedule Tab — hidden rather than unmounted when the user
                      switches to Notes, so an in-progress booking (chosen date,
                      MRI duration) survives the round trip. */}
                  <div style={{ display: modalTab === 'schedule' ? 'block' : 'none' }}>
                    <p><strong>Primary Phone:</strong> {selectedPatient.primary_phone || 'Not Available'}</p>
                    <p><strong>Cell Phone:</strong> {selectedPatient.cell_phone || 'Not Available'}</p>
                    <p><strong>Referring Provider:</strong> {selectedPatient.referring_provider || 'Not Available'}</p>
                    <div className="orderworklist-scheduling-form">
                      <SchedulingForm
                        patient={selectedPatient}
                        onSubmit={() => { setShowModal(false); fetchOrders(); }}
                      />
                    </div>
                  </div>

                  {/* Notes Tab */}
                  {modalTab === 'notes' && (
                    <div className="owlist-notes-panel">
                      <div className="owlist-note-form">
                        <textarea
                          className="owlist-note-textarea"
                          rows={3}
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Add a note (e.g. call attempt, scheduling conflict)..."
                        />
                        <button
                          className="owlist-note-submit"
                          onClick={handleAddNote}
                          disabled={addingNote || !newNote.trim()}
                        >
                          {addingNote ? 'Saving...' : 'Add Note'}
                        </button>
                      </div>

                      {notesLoading ? (
                        <p className="owlist-notes-empty">Loading notes...</p>
                      ) : orderNotes.length === 0 ? (
                        <p className="owlist-notes-empty">No notes yet for this order.</p>
                      ) : (
                        <div className="owlist-notes-list">
                          {orderNotes.map((n) => (
                            <div key={n.id} className="owlist-note-item">
                              <p className="owlist-note-text">{n.note}</p>
                              <p className="owlist-note-meta">
                                By: {n.created_by} &middot; {new Date(n.created_at).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    className="orderworklist-modal-close-button"
                    onClick={handleCloseModal}
                    disabled={addingNote}
                  >
                    {addingNote ? 'Saving...' : 'Close'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Cancel Modal ── */}
            {showCancelModal && selectedOrderForCancel && (
              <div className="orderworklist-modal-overlay">
                <div className="orderworklist-cancel-modal-content">
                  <div className="orderworklist-cancel-modal-header">
                    <h3>Cancel Order</h3>
                    <button
                      className="orderworklist-cancel-modal-x"
                      onClick={() => { setShowCancelModal(false); setCancelReason(''); setSelectedOrderForCancel(null); }}
                      disabled={cancelLoading}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="orderworklist-cancel-modal-body">
                    <div className="orderworklist-cancel-info-card">
                      <p><strong>Patient:</strong> {selectedOrderForCancel.first_name} {selectedOrderForCancel.last_name}</p>
                      <p><strong>Exam:</strong> {selectedOrderForCancel.exam_ordered}</p>
                      <p><strong>Date Ordered:</strong> {dateOfBirth(selectedOrderForCancel.created_at)}</p>
                    </div>
                    <div className="orderworklist-cancel-form">
                      <label htmlFor="cancel-reason">
                        Reason for Cancellation <span className="orderworklist-cancel-required">*</span>
                      </label>
                      <textarea
                        id="cancel-reason"
                        rows="4"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Please provide a reason for canceling this order..."
                        required
                      />
                    </div>
                  </div>

                  <div className="orderworklist-cancel-modal-footer">
                    <button
                      className="orderworklist-cancel-close-btn"
                      onClick={() => { setShowCancelModal(false); setCancelReason(''); setSelectedOrderForCancel(null); }}
                      disabled={cancelLoading}
                    >
                      Close
                    </button>
                    <button
                      className="orderworklist-confirm-cancel-button"
                      onClick={handleCancelOrder}
                      disabled={cancelLoading || !cancelReason.trim()}
                    >
                      {cancelLoading ? 'Canceling...' : 'Confirm Cancel'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderWorklist;
